import { api } from './api'
import {
  archiveReportSnapshotArtifact,
  getReportSnapshotArtifactDetail,
  listReportSnapshotArtifactSummaries,
  revokeReportSnapshotArtifact,
  type ArtifactStatus,
  type ArtifactVersionStatus,
  type ArtifactVisibility,
  type ReportSnapshotArtifactDetail,
  type ReportSnapshotArtifactSummary
} from './reportArtifactStorage'
import type {
  LotStoryArtifactBundleV1,
  LotStoryArtifactDetailV1,
  LotStoryArtifactVersionV1,
  LotStoryProjectionReadinessStatus,
  LotStoryProjectionV1
} from '../types/lotStory'

export type CollaborationArtifactSummary = ReportSnapshotArtifactSummary | LotStoryCollaborationArtifactSummary

export type LotStoryCollaborationArtifactSummary = {
  artifactId: string
  artifactType: 'lot-story'
  title: string
  contextLine: string
  summary: string
  visibility: ArtifactVisibility
  status: ArtifactStatus
  currentVersion: number
  ownerTenantKey: string
  publishedAt: string | null
  updatedAt: string
  sourceLabel: string
  sourceHref: string
  highlights: string[]
}

type CollaborationLifecycleEvent = {
  type: 'published' | 'revoked' | 'archived'
  at: string
  version: number | null
}

type CollaborationArtifactOverview = Omit<LotStoryCollaborationArtifactSummary, 'artifactId' | 'artifactType' | 'highlights' | 'sourceLabel' | 'sourceHref'>

type CollaborationArtifactVersion = {
  version: number
  status: ArtifactVersionStatus
  publishedAt: string | null
}

type CollaborationArtifactActions = {
  canCreateDraft: boolean
  canRefreshDraftSnapshot: boolean
  canPublish: boolean
  canRevoke: boolean
  canArchive: boolean
}

export type LotStoryCollaborationArtifactDetail = {
  artifactId: string
  artifactType: 'lot-story'
  overview: CollaborationArtifactOverview
  snapshotPreview: {
    kind: 'lot-story'
    title: string
    summary: string
    notes: string
    generatedAt: string
    readinessStatus: LotStoryProjectionReadinessStatus
    projection: LotStoryProjectionV1
  }
  provenance: {
    sourceRefs: Array<{
      domain: 'lots'
      entityId: string
      label: string
    }>
    publishedBy: {
      tenantKey: string
      displayName: string
    } | null
    lifecycleEvents: CollaborationLifecycleEvent[]
  }
  versions: CollaborationArtifactVersion[]
  source: {
    label: string
    href: string
  }
  actions: CollaborationArtifactActions
}

export type CollaborationArtifactDetail = ReportSnapshotArtifactDetail | LotStoryCollaborationArtifactDetail

const normalizeOptionalString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const getLotStoryFamilyLabel = (projection: LotStoryProjectionV1): string => (
  normalizeOptionalString(projection.identityContext.lineageOrigin?.lotKey)
  ?? normalizeOptionalString(projection.identityContext.activeDescendants[0]?.lotKey)
  ?? normalizeOptionalString(projection.lineageRootId)
  ?? '—'
)

const formatOptionalDateTime = (value: string | null | undefined): string | null => {
  const trimmed = normalizeOptionalString(value)
  if (!trimmed) return null

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toLocaleString()
}

const capitalize = (value: string): string => (
  value.length > 0
    ? value.slice(0, 1).toUpperCase() + value.slice(1)
    : value
)

const resolvePublishedLotStoryVersion = (
  artifact: Pick<LotStoryArtifactDetailV1['artifact'], 'currentPublishedVersionId'>,
  versions: LotStoryArtifactVersionV1[]
): LotStoryArtifactVersionV1 | null => {
  const currentPublishedVersionId = normalizeOptionalString(artifact.currentPublishedVersionId)
  if (currentPublishedVersionId) {
    const explicitVersion = versions.find((version) => (
      version.artifactVersionId === currentPublishedVersionId && version.versionStatus === 'published'
    ))
    if (explicitVersion) {
      return explicitVersion
    }
  }

  return versions.find((version) => version.versionStatus === 'published') ?? null
}

const isPublishedGroupLotStory = (artifact: LotStoryArtifactBundleV1['artifact']): boolean => (
  artifact.visibility === 'group'
  && artifact.status === 'published'
  && Boolean(normalizeOptionalString(artifact.currentPublishedVersionId))
)

const buildLotStoryHighlights = (version: LotStoryArtifactVersionV1): string[] => {
  const publishedAt = formatOptionalDateTime(version.publishedAt)

  return [
    `Readiness: ${capitalize(version.readinessStatus)}`,
    `Evidence: Feed ${version.projectionSnapshot.evidencePackage.feedRefs.length} · Water ${version.projectionSnapshot.evidencePackage.waterRefs.length} · Production ${version.projectionSnapshot.evidencePackage.productionRefs.length}`,
    publishedAt ? `Published: ${publishedAt}` : null
  ].filter((value): value is string => Boolean(value))
}

const resolveLotStoryPublishedByLabel = (version: LotStoryArtifactVersionV1): string | null => (
  normalizeOptionalString(version.publishedByContext?.communityDisplayName)
  ?? normalizeOptionalString(version.publishedByContext?.accountDisplayName)
  ?? normalizeOptionalString(version.publishedByContext?.actorId)
)

const buildLotStorySourceHref = (artifactId: string, lineageRootId: string): string => {
  const params = new URLSearchParams({
    workspace: 'stories',
    storyArtifactId: artifactId,
    lineageRootId
  })

  return `/lots?${params.toString()}`
}

const isCollaborationArtifactVersionStatus = (
  status: LotStoryArtifactVersionV1['versionStatus']
): status is ArtifactVersionStatus => (
  status === 'published' || status === 'superseded' || status === 'revoked'
)

const mapLotStorySummary = (
  artifact: LotStoryArtifactBundleV1['artifact'],
  version: LotStoryArtifactVersionV1
): LotStoryCollaborationArtifactSummary => {
  const storyFamilyLabel = getLotStoryFamilyLabel(version.projectionSnapshot)
  const versionSummary = normalizeOptionalString(version.summary)

  return {
    artifactId: artifact.artifactId,
    artifactType: 'lot-story',
    title: version.title,
    contextLine: storyFamilyLabel,
    summary: versionSummary ?? `Published lot story snapshot for ${storyFamilyLabel}.`,
    visibility: 'group',
    status: artifact.status,
    currentVersion: version.versionNumber,
    ownerTenantKey: artifact.tenantKey,
    publishedAt: normalizeOptionalString(version.publishedAt),
    updatedAt: artifact.updatedAt,
    sourceLabel: 'Lots',
    sourceHref: buildLotStorySourceHref(artifact.artifactId, artifact.lineageRootId),
    highlights: buildLotStoryHighlights(version)
  }
}

const resolveLotStorySummary = async (bundle: LotStoryArtifactBundleV1): Promise<LotStoryCollaborationArtifactSummary | null> => {
  if (!isPublishedGroupLotStory(bundle.artifact)) {
    return null
  }

  if (
    bundle.version.versionStatus === 'published'
    && bundle.version.artifactVersionId === bundle.artifact.currentPublishedVersionId
  ) {
    return mapLotStorySummary(bundle.artifact, bundle.version)
  }

  const detail = await api.lotStories.get(bundle.artifact.artifactId)
  const publishedVersion = resolvePublishedLotStoryVersion(detail.artifact, detail.versions)
  return publishedVersion ? mapLotStorySummary(detail.artifact, publishedVersion) : null
}

export const listCollaborationArtifactSummaries = async (): Promise<CollaborationArtifactSummary[]> => {
  const reportSummaries = listReportSnapshotArtifactSummaries()

  let lotStorySummaries: LotStoryCollaborationArtifactSummary[] = []
  try {
    const lotStoryBundles = await api.lotStories.list()
    lotStorySummaries = (await Promise.all(lotStoryBundles.map((bundle) => resolveLotStorySummary(bundle))))
      .filter((value): value is LotStoryCollaborationArtifactSummary => Boolean(value))
  } catch {
    lotStorySummaries = []
  }

  return [...reportSummaries, ...lotStorySummaries]
    .slice()
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
}

const sortLifecycleEvents = (events: CollaborationLifecycleEvent[]): CollaborationLifecycleEvent[] => {
  const eventPriority: Record<CollaborationLifecycleEvent['type'], number> = {
    revoked: 3,
    archived: 2,
    published: 1
  }

  return events.sort((left, right) => {
    if (left.at === right.at) {
      if ((left.version ?? 0) !== (right.version ?? 0)) {
        return (right.version ?? 0) - (left.version ?? 0)
      }

      return eventPriority[right.type] - eventPriority[left.type]
    }

    return right.at.localeCompare(left.at)
  })
}

const buildLotStoryLifecycleEvents = (detail: LotStoryArtifactDetailV1): CollaborationLifecycleEvent[] => {
  const events: CollaborationLifecycleEvent[] = detail.versions.flatMap((version) => (
    version.versionStatus === 'published' && Boolean(normalizeOptionalString(version.publishedAt))
      ? [{
          type: 'published' as const,
          at: version.publishedAt ?? detail.artifact.updatedAt,
          version: version.versionNumber
        }]
      : []
  ))

  if (detail.artifact.revokedAt) {
    events.push({
      type: 'revoked',
      at: detail.artifact.revokedAt,
      version: resolvePublishedLotStoryVersion(detail.artifact, detail.versions)?.versionNumber ?? null
    })
  }

  return sortLifecycleEvents(events)
}

const mapLotStoryDetail = (detail: LotStoryArtifactDetailV1): LotStoryCollaborationArtifactDetail | null => {
  if (detail.artifact.visibility !== 'group' || detail.artifact.status !== 'published') {
    return null
  }

  const version = resolvePublishedLotStoryVersion(detail.artifact, detail.versions)
  if (!version) {
    return null
  }

  const storyFamilyLabel = getLotStoryFamilyLabel(version.projectionSnapshot)
  const versionSummary = normalizeOptionalString(version.summary)
  const versionNotes = normalizeOptionalString(version.notes)
  const publishedBy = resolveLotStoryPublishedByLabel(version)

  return {
    artifactId: detail.artifact.artifactId,
    artifactType: 'lot-story',
    overview: {
      title: version.title,
      contextLine: storyFamilyLabel,
      summary: versionSummary ?? `Published lot story snapshot for ${storyFamilyLabel}.`,
      visibility: 'group',
      status: detail.artifact.status,
      currentVersion: version.versionNumber,
      ownerTenantKey: detail.artifact.tenantKey,
      publishedAt: normalizeOptionalString(version.publishedAt),
      updatedAt: detail.artifact.updatedAt
    },
    snapshotPreview: {
      kind: 'lot-story',
      title: version.title,
      summary: versionSummary ?? '',
      notes: versionNotes ?? '',
      generatedAt: version.projectionGeneratedAt,
      readinessStatus: version.readinessStatus,
      projection: version.projectionSnapshot
    },
    provenance: {
      sourceRefs: [{
        domain: 'lots',
        entityId: detail.artifact.lineageRootId,
        label: storyFamilyLabel
      }],
      publishedBy: publishedBy
        ? {
            tenantKey: detail.artifact.tenantKey,
            displayName: publishedBy
          }
        : null,
      lifecycleEvents: buildLotStoryLifecycleEvents(detail)
    },
    versions: detail.versions
      .slice()
      .sort((left, right) => right.versionNumber - left.versionNumber)
      .flatMap((versionCandidate) => {
        if (!isCollaborationArtifactVersionStatus(versionCandidate.versionStatus)) {
          return []
        }

        return [{
          version: versionCandidate.versionNumber,
          status: versionCandidate.versionStatus,
          publishedAt: normalizeOptionalString(versionCandidate.publishedAt)
        }]
      }),
    source: {
      label: 'Open in Lots',
      href: buildLotStorySourceHref(detail.artifact.artifactId, detail.artifact.lineageRootId)
    },
    actions: {
      canCreateDraft: false,
      canRefreshDraftSnapshot: false,
      canPublish: false,
      canRevoke: false,
      canArchive: false
    }
  }
}

export const loadCollaborationArtifactDetail = async (artifactId: string): Promise<CollaborationArtifactDetail | null> => {
  const reportDetail = getReportSnapshotArtifactDetail(artifactId)
  if (reportDetail) {
    return reportDetail
  }

  try {
    const lotStoryDetail = await api.lotStories.get(artifactId)
    return mapLotStoryDetail(lotStoryDetail)
  } catch {
    return null
  }
}

export const revokeCollaborationArtifact = async (detail: CollaborationArtifactDetail): Promise<CollaborationArtifactDetail | null> => {
  if (detail.artifactType !== 'report-snapshot') {
    return null
  }

  return revokeReportSnapshotArtifact(detail.artifactId)
}

export const archiveCollaborationArtifact = async (detail: CollaborationArtifactDetail): Promise<CollaborationArtifactDetail | null> => {
  if (detail.artifactType !== 'report-snapshot') {
    return null
  }

  return archiveReportSnapshotArtifact(detail.artifactId)
}